import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext'; 
import toast from 'react-hot-toast';
import './PaymentStatus.css';

// --- Komponen Paparan Ikut Status (ClassNames asal dikekalkan) ---

const LoadingView = () => (
  <div className="status-verifying">
    <div className="loading-spinner"></div>
    <h2>Verifying Your Payment</h2>
    <p>Please wait while we confirm your payment details...</p>
  </div>
);

const SuccessView = ({ orderDetails, handleBackToHome, handleViewOrders, formatDate }) => (
  <div className="status-success">
    <div className="success-icon">✅</div>
    <h2>Payment Successful!</h2>
    <p className="confirmation-message">
      Your payment has been successfully processed. Please click the <strong>"View My Orders"</strong> button below to retrieve your purchased digital codes.
    </p>
    {orderDetails && (
      <div className="order-details">
        <h3>Order Summary</h3>
        <div className="detail-row">
          <span className="detail-label">Order ID:</span>
          <span className="detail-value">{orderDetails?.order_id || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Total Amount:</span>
          <span className="detail-value">{orderDetails?.total ? `RM${orderDetails.total}` : 'N/A'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Order Date:</span>
          <span className="detail-value">{formatDate(orderDetails?.date)}</span>
        </div>
      </div>
    )}
    <div className="action-buttons">
      <button className="btn-primary" onClick={() => handleViewOrders(orderDetails?.order_id)}>
        View My Orders
      </button>
      <button className="btn-secondary" onClick={handleBackToHome}>
        Continue Shopping
      </button>
    </div>
  </div>
);

const FailedView = ({ errorMessage, handleRetryPayment, handleBackToHome }) => (
  <div className="status-failed">
    <div className="failed-icon">❌</div>
    <h2>Payment Unsuccessful</h2>
    
    <div className="error-box" style={{ textAlign: 'left', margin: '20px 0', padding: '15px', backgroundColor: '#fff5f5', borderLeft: '4px solid #e53e3e', borderRadius: '4px' }}>
      <p style={{ fontSize: '11px', color: '#e53e3e', margin: '0 0 5px 0', fontWeight: 'bold', textTransform: 'uppercase' }}>Gateway Response:</p>
      <p style={{ fontSize: '14px', color: '#2d3748', margin: 0, fontWeight: '500' }}>
        {errorMessage || 'The transaction was declined or cancelled by the user/bank.'}
      </p>
    </div>

    <p>Please verify your account balance, check your bank status, or try using an alternative payment method.</p>
    
    <div className="action-buttons">
      <button className="btn-primary" onClick={handleRetryPayment}>
        Try Payment Again
      </button>
      <button className="btn-secondary" onClick={handleBackToHome}>
        Back to Home
      </button>
    </div>
  </div>
);

const PendingView = ({ orderDetails, handleBackToHome, errorMessage }) => (
  <div className="status-failed">
    <div className="pending-icon" style={{ fontSize: '48px', marginBottom: '15px' }}>
      <div className="loading-spinner" style={{ margin: '0 auto 15px auto' }}></div>
    </div>
    <h2>Verifying Payment Status...</h2>
    
    <div className="info-box" style={{ textAlign: 'left', margin: '20px 0', padding: '15px', backgroundColor: '#fffaf0', borderLeft: '4px solid #dd6b20', borderRadius: '4px' }}>
      <p style={{ fontSize: '11px', color: '#dd6b20', margin: '0 0 5px 0', fontWeight: 'bold', textTransform: 'uppercase' }}>System Information:</p>
      <p style={{ fontSize: '14px', color: '#2d3748', margin: 0, fontWeight: '500' }}>
        If funds have been deducted from your account, please do not close or refresh this page. We are continuously synchronizing with ToyyibPay to finalize your secure delivery.
      </p>
    </div>

    <p>This screen will automatically redirect to "Successful" as soon as the banking network releases the transaction clearance.</p>
    
    {orderDetails && (
      <div className="order-details" style={{ marginTop: '20px' }}>
        <h3>Order Summary</h3>
        <div className="detail-row">
          <span className="detail-label">Order ID:</span>
          <span className="detail-value">{orderDetails?.order_id || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Total Amount:</span>
          <span className="detail-value">{orderDetails?.total ? `RM${orderDetails.total}` : 'N/A'}</span>
        </div>
      </div>
    )}
    <div className="action-buttons">
      <button className="btn-primary" onClick={() => window.location.reload()}>
        Force Refresh
      </button>
      <button className="btn-secondary" onClick={handleBackToHome}>
        Back to Home
      </button>
    </div>
  </div>
);

const ErrorView = ({ verifyPayment, handleContactSupport, errorMessage }) => (
  <div className="status-error">
    <div className="error-icon">⚠️</div>
    <h2>Verification Error</h2>
    <p>{errorMessage || 'We encountered a technical issue while validating your payment deployment.'}</p>
    <div className="action-buttons">
      <button className="btn-primary" onClick={verifyPayment}>
        Re-verify
      </button>
      <button className="btn-secondary" onClick={handleContactSupport}>
        Contact Support
      </button>
    </div>
  </div>
);


// --- Komponen Utama Payment Status ---

const PaymentStatus = () => {
  const location = useLocation();
  const history = useHistory();
  const { token } = useAuth();
  const { API_BASE_URL } = useApi(); 

  const [state, setState] = useState({
    status: 'verifying',
    orderDetails: null,
    isLoading: true,
    errorMessage: null,
  });

  // Kaunter pusingan untuk sistem dinamik polling (kawal beban server)
  const [retryCount, setRetryCount] = useState(0);

  const handlePaymentSuccess = useCallback((order) => {
    setState(prevState => ({
      ...prevState,
      status: 'success',
      orderDetails: { ...order },
      isLoading: false,
      errorMessage: null,
    }));
  }, []);

  const handlePaymentFailure = useCallback((msg) => {
    setState(prevState => ({
      ...prevState,
      status: 'failed',
      orderDetails: {
        ...prevState.orderDetails,
        status: 'failed',
        error_message: msg,
      },
      isLoading: false,
      errorMessage: msg,
    }));
  }, []);

  const verifyPayment = useCallback(async () => {
    // Sentiasa set isLoading true setiap kali pusingan baru bermula untuk lock Toast awal
    setState(prevState => ({ ...prevState, isLoading: true }));

    const params = new URLSearchParams(location.search);
    const billcode = params.get('billcode') || params.get('billCode');

    if (!API_BASE_URL) {
      setState(prevState => ({
        ...prevState,
        status: 'error',
        isLoading: false,
        errorMessage: 'Configuration error. API URL is missing.',
      }));
      return;
    }

    if (!billcode) {
      setState(prevState => ({
        ...prevState,
        status: 'error',
        isLoading: false,
        errorMessage: 'No payment information found (Missing billCode).',
      }));
      return;
    }
    
    const VERIFY_API = `${API_BASE_URL}/payment/verify/${billcode}`;

    try {
      const response = await fetch(VERIFY_API, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Payment verification failed');
      }
      
      const billData = data.data || {};
      const order = {
        order_id: billData.billExternalReferenceNo || billcode || 'N/A',
        total: billData.billpaymentAmount ? parseFloat(billData.billpaymentAmount) : 0,
        date: billData.billPaymentDate || 'N/A',
        transactionId: billData.billpaymentInvoiceNo || 'N/A',
      };

      if (data.status === 'success') {
        handlePaymentSuccess(order);
      } else if (data.status === 'pending') {
        setState(prevState => ({
          ...prevState,
          status: 'pending',
          orderDetails: order,
          isLoading: false,
          errorMessage: data.message || null
        }));

        // 🔄 LOGIK INFINITE POLLING PINTAR (Kalis Beban Server)
        // 5 pusingan pertama check setiap 4 saat. Seterusnya, kurangkan laju ke 10 saat sekali.
        const nextDelay = retryCount < 5 ? 4000 : 10000;

        setTimeout(() => {
          setRetryCount(prev => prev + 1); // Trigger semula useEffect utama
        }, nextDelay);

      } else {
        handlePaymentFailure(data.message || 'Payment failed or was cancelled');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      handlePaymentFailure(error.message);
    }
  }, [location.search, token, API_BASE_URL, handlePaymentSuccess, handlePaymentFailure, retryCount]);

  // Pengurusan keluar masuk mesej Toast amaran
  useEffect(() => {
    if (!state.isLoading) {
      if (state.status === 'success') {
        toast.success('Payment successful! Your order has been confirmed.');
      } else if (state.status === 'failed') {
        toast.error(state.errorMessage || 'Payment failed. Please try again.');
      } else if (state.status === 'error') {
        toast.error(state.errorMessage || 'An error occurred during verification.');
      }
    }
  }, [state.status, state.isLoading, state.errorMessage]);

  // Trigger utama untuk jalankan semakan API bila page load atau retryCount berubah
  useEffect(() => {
    if (API_BASE_URL) {
      verifyPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, retryCount]); 

  const handleBackToHome = () => history.push('/product');
  const handleRetryPayment = () => history.push('/checkout');
  
  const handleViewOrders = (orderId) => {
    if (orderId && orderId !== 'N/A') {
      history.push(`/dashboard?page=viewOrder&orderId=${orderId}`);
    } else {
      history.push('/dashboard?page=viewOrder');
    }
  };
  
  const handleContactSupport = (e) => {
    e.preventDefault();
    window.location.href = 'mailto:support@modbymuse.com';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      if (dateString.includes('-') && dateString.includes(':')) {
        const parts = dateString.split(/[\s-:]/);
        if (parts.length >= 6) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; 
          const year = parseInt(parts[2], 10);
          const hours = parseInt(parts[3], 10);
          const minutes = parseInt(parts[4], 10);
          const seconds = parseInt(parts[5], 10);
          return new Date(year, month, day, hours, minutes, seconds).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
          });
        }
      }
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
      }
    } catch (e) {
      console.error("Format date error:", e);
    }
    return dateString;
  };

  return (
    <div className="payment-status-container">
      <div className="payment-status-card">
        {state.status === 'verifying' && <LoadingView />}
        
        {state.status === 'success' && (
          <SuccessView
            orderDetails={state.orderDetails}
            handleBackToHome={handleBackToHome}
            handleViewOrders={handleViewOrders}
            formatDate={formatDate}
          />
        )}
        
        {state.status === 'failed' && (
          <FailedView
            errorMessage={state.errorMessage}
            handleRetryPayment={handleRetryPayment}
            handleBackToHome={handleBackToHome}
          />
        )}
        
        {state.status === 'pending' && (
          <PendingView
            orderDetails={state.orderDetails}
            handleBackToHome={handleBackToHome}
            errorMessage={state.errorMessage}
          />
        )}
        
        {state.status === 'error' && (
          <ErrorView
            verifyPayment={verifyPayment}
            handleContactSupport={handleContactSupport}
            errorMessage={state.errorMessage}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;